<template>
    <div class="dashboard-head">
        <div class="flex flex-wrap items-center gap-2">
            <select class="form-control period-select" v-model="period">
                <option value="hour">{{ $t('message.hour') }}</option>
                <option value="day">{{ $t('message.day') }}</option>
                <option value="month">{{ $t('message.month') }}</option>
                <option value="year">{{ $t('message.year') }}</option>
            </select>
            <b-btn class="angle" @click="sub"><i class="fa fa-angle-left"></i></b-btn>
            <date-picker class="period-select" :class="{'focus-box':focus}" v-model="date" :config="config" ref="picker"></date-picker>
            <select v-if="period === 'hour'" class="form-control hour-selector" v-model="hour">
                <option v-for="i in Array(24).keys()" :value="i">
                    {{i}} - {{i+1}}
                </option>
            </select>
            <b-btn  class="angle" @click="add"><i class="fa fa-angle-right"></i></b-btn>
            <i v-if="loading" class="fa fa-spin fa-spinner"></i>
        </div>
        <div>
            <small class="text-sm text-muted whitespace-nowrap">{{ $t('message.data_loaded') }}: {{currentDate}}</small> <button @click="reloadData" class="btn btn-default btn-sm ml-4">{{ $t('message.reload') }}</button>
        </div>
        <div>
            <b>{{ $t('message.show') }}: </b>
            <input type="radio" value="liters" v-model="showUnit"> {{ $t('message.' + unitToShow) }}
            <input type="radio" value="price" v-model="showUnit"> {{ $t('message.price_estimate') }}
        </div>
    </div>
</template>

<script>
    import { DatePicker as datePicker } from '../../ui'
    import moment from 'moment'
    import 'moment-timezone'
    import _ from 'lodash'
    import mixins from '../../modules/mixins'
    import eventBus from '../../modules/eventBus'

    const format = {
        'hour': 'DD.MM.YYYY',
        'day': 'DD.MM.YYYY',
        'month': 'MM.YYYY',
        'year': 'YYYY'
    }

    const checkDate = (date) => {
        if (typeof date === 'string') {
            let sformat = false
            if ((date.match(/\./g) || []).length === 2 && (date.match(/-/g) || []).length === 1) {
                sformat = format['hour']
            } else if ((date.match(/\./g) || []).length === 2 && (date.match(/-/g) || []).length === 0) {
                sformat = format['day']
            } else if ((date.match(/\./g) || []).length === 1) {
                sformat = format['month']
            } else if ((date.match(/\./g) || []).length === 0) {
                sformat = format['year']
            }

            if (sformat) {
                date = moment(date + ' +00:00', sformat + ' ZZ')
            }
        }
        const currentYear = moment().year()
        return moment.utc([date.year() || currentYear, (date.month() + 1 || 1) - 1, date.date() || 1])
    }

    const parseFilter = (dateStr, period) => {
        let hour = moment().get('hour')
        let raw = dateStr
        if (period === 'hour' && typeof dateStr === 'string' && dateStr.indexOf('-') !== -1) {
            const parts = dateStr.split('-')
            raw = parts[0]
            const parsedHour = parseInt(parts[1], 10)
            if (!Number.isNaN(parsedHour)) {
                hour = parsedHour
            }
        }
        const fmt = format[period] || format.day
        const parsed = raw ? moment(raw + ' +00:00', fmt + ' ZZ') : moment()
        return {
            date: parsed.isValid() ? parsed : moment(),
            hour
        }
    }

    export default {
        name: 'dashboard-head',
        components: {
            datePicker
        },
        mixins: [mixins],
        props: {
            type: {
                type: String,
                default: 'total'
            },
            unit: {
                type: String,
                default: 'liters'
            },
            filterDate: {
                type: String,
                default: ''
            },
            filterPeriod: {
                type: String,
                default: 'day'
            }
        },
        data () {
            const period = this.filterPeriod || 'day'
            const parsed = parseFilter(this.filterDate, period)
            return {
                unitToShow: 'liters',
                currentDate: '',
                period: period,
                hour: parsed.hour,
                loading: false,
                syncingFromProps: false,
                date: parsed.date,
                focus: false,
                config: {
                    format: format[period] || format.day,
                    useCurrent: false,
                    locale: 'cs',
                    maxDate: moment(),
                    useStrict: true,
                    icons: {
                        time: 'fa fa-clock',
                        date: 'fa fa-calendar-alt',
                        up: 'fa fa-chevron-up',
                        down: 'fa fa-chevron-down',
                        previous: 'fa fa-chevron-left',
                        next: 'fa fa-chevron-right',
                        today: 'fa fa-dot-circle',
                        clear: 'fa fa-trash-alt',
                        close: 'fa fa-times'
                    }
                }
            }
        },
        filters: {
            round (int) {
                const n = Number(int)
                if (Number.isNaN(n)) return int
                return Math.round(n * 100) / 100
            }
        },
        watch: {
            hour () {
                if (this.syncingFromProps) {
                    return
                }
                if (this.date == null) {
                    return
                }
                this.emitChange(this.date, this.period)
            },
            date () {
                if (this.syncingFromProps) {
                    return
                }
                if (this.date == null) {
                    return
                }
                this.emitChange(this.date, this.period)
            },
            period (period) {
                this.updateUnitToShow()
                if (this.syncingFromProps) {
                    this.config = _.defaults({
                        format: format[period]
                    }, this.config)
                    return
                }
                const date = checkDate(this.date)
                this.date = null
                this.config = _.defaults({
                    format: format[period]
                }, this.config)
                this.date = date
            },
            filterDate () {
                this.syncFromProps()
            },
            filterPeriod () {
                this.syncFromProps()
            },
            unit () {
                this.updateUnitToShow()
            }
        },
        methods: {
            updateUnitToShow () {
                if (this.period === 'year' && this.unit === 'liters') {
                    this.unitToShow = 'm3'
                } else {
                    this.unitToShow = (this.unit || 'liters').replace('watts', 'power')
                }
            },
            formattedFilter () {
                if (!this.date || !this.date.format) {
                    return ''
                }
                let formatted = this.date.format(format[this.period] || format.day)
                if (this.period === 'hour') {
                    formatted += '-' + this.hour
                }
                return formatted
            },
            syncFromProps () {
                const period = this.filterPeriod || 'day'
                const incoming = this.filterDate || ''
                if (period === this.period && incoming === this.formattedFilter()) {
                    return
                }
                this.syncingFromProps = true
                this.period = period
                const parsed = parseFilter(incoming, period)
                this.hour = parsed.hour
                this.config = _.defaults({
                    format: format[period] || format.day
                }, this.config)
                this.date = parsed.date
                this.$nextTick(() => {
                    this.syncingFromProps = false
                })
            },
            reloadData () {
                eventBus.emit('change', {force: true, reload: true})
            },
            emitChange (date, period) {
                let formatedDate = date.format(format[period])
                if (period === 'hour') {
                    formatedDate += '-' + this.hour
                }
                const req = {
                    type: this.type,
                    date: formatedDate,
                    period: period,
                    source: 'datepicker'
                }
                eventBus.emit('change', req)
            },
            updateCurrentDate () {
                this.currentDate = moment().tz(this.activeOrganization.timezone).format('LLL zz')
            },
            add () {
                // TODO limit
                if (this.period === 'hour' && this.hour < 23) {
                    this.hour++
                    return
                }
                const date = checkDate(this.date)
                this.date = null
                const period = this.period === 'hour' ? 'day' : this.period

                if (this.period === 'hour') {
                    this.hour = 0
                }
                this.date = date.clone().add(1, period + 's')
            },
            sub () {
                if (this.period === 'hour' && this.hour > 0) {
                    this.hour--
                    return
                }
                const date = checkDate(this.date)
                this.date = null
                const period = this.period === 'hour' ? 'day' : this.period
                const newDate = date.clone().subtract(1, period + 's')
                if (this.period === 'hour') {
                    this.hour = 23
                }
                this.date = newDate
            }
        },
        created () {
            this.updateUnitToShow()
            if (this.type === 'total') {
                this.loading = true
            }
            this.stopLoadingStateListener = eventBus.on('loading-state', (state) => {
                this.loading = state.state
                this.updateCurrentDate()
            })
            this.stopFocusDateListener = eventBus.on('focus-date', () => {
                this.focus = true
                setTimeout(() => {
                    this.focus = false
                }, 200)
            })
        },
        beforeUnmount () {
            this.stopLoadingStateListener()
            this.stopFocusDateListener()
        }
    }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
    .head {
        display: inline-block;
    }

    .dashboard-head {
        margin-top: 20px;
        position: relative;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        flex-wrap: wrap
    }

    .period-select {
        display: inline-block !important;
        width: 200px !important;
    }

    .bootstap-datepicker-widget {
        position: relative;
    }

    .angle {
        vertical-align: middle;
        min-width: 30px;
        padding-left: 8px;
        padding-right: 8px;
    }

    .focus-box {
        border-color: #ccc;
        border-color: rgba(82,168,236,.8);
        outline: 0;
        outline: thin dotted\9;
        -webkit-box-shadow: 0 0 8px rgba(82,168,236,.6);
        box-shadow: 0 0 8px rgba(82,168,236,.6);
    }

    .hour-selector {
        display: inline-block !important;
        max-width: 100px;
    }
</style>
